import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { CustomHeading, Span, StageSelector } from 'shared-components';
import { ITag } from '../../../types';
import { UserRole } from '../../../store/auth/types';
import { Status } from '../../../store/student/core/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import StudentHeaderTags from './Tags/index';

interface Props {
  role?: UserRole;
  givenName?: string;
  middleName?: string;
  surname?: string;
  studentNo: string;
  university: string;
  intakeId: string;
  offerId: string;
  tags: ITag[];
  allTags: ITag[];
  allProgrammableTags: ITag[];
  isPathway: boolean;
  isPathwayStudent: boolean;
  isPathwayProvider: boolean;
  currentStage: Status;
  configTabs: Array<Status>;
  processedByUni?: boolean;
  onStageChange: (status: Status) => void;
  onClose: () => void;
  unpinTagsFromStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
  pinTagsToStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
}

const NYE = 'Not Yet Entered';

function StudentHeader(props: Props) {
  const {
    role,
    givenName = NYE,
    middleName,
    surname,
    studentNo,
    university,
    intakeId,
    offerId,
    onClose,
    tags,
    unpinTagsFromStudents,
    pinTagsToStudents,
    isPathway,
    allTags,
    allProgrammableTags,
    isPathwayStudent,
    isPathwayProvider,
    currentStage,
    configTabs,
    processedByUni,
    onStageChange,
  } = props;

  const canViewTags = role && hasPerm(role, P.TAGS_VIEW);
  const canEditStudentCurrentStage = hasPerm(role, P.STUDENT_STATUS_EDIT);

  return (
    <Box as="header" mb={6}>
      <Breadcrumb mb={4} separator={<FontAwesomeIcon className="utility-icon" icon={faChevronRight} />}>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={onClose} id="studentHeader-closeButton">
            Students
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink data-selector="studentHeader-studentNo" color={'inherit'} _hover={{ color: 'inherit' }}>
            {studentNo}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex mb={6} flexWrap={'wrap'} rowGap={4} columnGap={3}>
        <CustomHeading data-selector="studentHeader-name" level="h1" flex={1} mb={0} sx={{ wordBreak: 'break-word' }}>
          <Span fontWeight={'normal'}>
            {givenName} {middleName}
          </Span>{' '}
          {surname}
        </CustomHeading>

        {canEditStudentCurrentStage ? (
          <StageSelector
            isPathwayProvider={isPathwayProvider}
            isPathwayStudent={isPathwayStudent}
            currentStage={currentStage}
            configTabs={configTabs}
            onStageChange={onStageChange}
            processedByUni={processedByUni}
          />
        ) : null}
      </Flex>

      {canViewTags && (
        <StudentHeaderTags
          role={role}
          tags={tags}
          isPathway={isPathway}
          studentNo={studentNo}
          university={university}
          intakeId={intakeId}
          offerId={offerId}
          unpinTagsFromStudents={unpinTagsFromStudents}
          pinTagsToStudents={pinTagsToStudents}
          allProgrammableTags={allProgrammableTags}
          allTags={allTags}
        />
      )}
    </Box>
  );
}

export default StudentHeader;
